import { Icon } from '@doordash/design-language'
import { CountryCode } from '@doordash/lib-i18n'
import * as React from 'react'
import { connect } from 'react-redux'
import {
  ANDROID_CLOSE_DEEPLINK,
  COUNTRY_SHORTCODES,
  IOS_CLOSE_DEEPLINK,
} from '../../../constants'
import { detectWebviewOs } from '../../../utilities/userAgent'
import { OsTypes } from '../../../webview/types'
// tslint:disable-next-line:max-line-length
import {
  GovernmentId,
  VerificationComplete,
  VerificationInProgress,
  VerifyIdentity,
} from '../../atoms/bank-onboarding-flow'
// tslint:disable-next-line:max-line-length
import IdentificationNumber from '../../atoms/bank-onboarding-flow/identification-number'
import SubmitPhotoId from '../../atoms/bank-onboarding-flow/submit-photo-id'
// tslint:disable-next-line:max-line-length
import SubmitPhotoIdBack from '../../atoms/bank-onboarding-flow/submit-photo-id-back'
import VerifyAddress from '../../atoms/bank-onboarding-flow/verify-address'

import * as actions from '../../../actions'

import * as events from '../../../events'
import {
  BankAdditionalVerification as DataType,
  BankAdditionalVerificationPages as PageEnum,
  EventActions,
} from '../../../typeModules/events'

import { Application } from 'src/utilities/application/application'
import { Applicant, Photo, StripeCredentials } from '../../../models/types'
import { Steps } from './constants'
import { CloseButton, Content } from './styles'

type Props = {
  application: Application
  uniqueLink: string
  country?: CountryCode
  skipIntro?: boolean
  stripeCredentials?: StripeCredentials
  getStripeCredentials?: (dasherId: string) => Promise<any>
  getPaymentAccount?: (dasherId: string) => Promise<any>
  getApplicationStatus?: (uniqueLink: string) => any
  trackBankAdditionalVerification?: (data: DataType) => Promise<any>
  documentsRequired: any
  handleDismiss?: () => void
  clearErrors?: () => void
  isDismissible: boolean
}

type State = {
  currentStep: number
  stripe: any
  photoIdFront: Photo
  photoIdBack: Photo
}

export class BankAdditionalVerification extends React.Component<Props, State> {
  private stepUpperBound: number
  private stepList: PageEnum[]
  private country: CountryCode
  private applicant: Applicant
  public constructor(props) {
    super(props)

    const { country } = this.props

    const stepList =
      country && Steps.hasOwnProperty(country) ? Steps[country] : Steps.US
    this.stepUpperBound = stepList.length
    this.stepList = stepList
    this.country = country

    this.applicant = {
      uniqueLink: props.uniqueLink,
      firstName: props.application.firstName,
      email: props.application.email,
    }
  }

  public componentWillMount() {
    const { documentsRequired, skipIntro } = this.props
    const step = skipIntro ? [] : [PageEnum.VerifyIdentity]
    let addressPushed = false
    // Currently, Stripe sends us back documents with these keywords.
    // We're mapping these keywords with verification pages to show the user
    documentsRequired.forEach(document => {
      // The address payload has multiple fields so
      // pushing 1 page to submit all address fields
      if (!addressPushed && document.includes('address')) {
        addressPushed = true
        step.push(PageEnum.VerifyAddress)
        return
      }
      if (
        document.includes('personal_id_number') ||
        document.includes('ssn_last_4')
      ) {
        step.push(PageEnum.IdentificationNumber)
        return
      }
      if (document.includes('document')) {
        if (document.includes('document_back')) {
          step.push(PageEnum.SubmitPhotoIdBack)
          return
        }
        step.push(PageEnum.GovernmentId)
        step.push(PageEnum.SubmitPhotoId)
      }
    })

    step.push(PageEnum.VerificationInProgress)

    // Need hooks of some sort for the verification complete page
    // step.push(PageEnum.VerificationComplete)

    this.stepList = step
  }

  // tslint:disable-next-line:member-ordering
  public state = {
    currentStep: 0,
    stripe: null,
    photoIdFront: null,
    photoIdBack: null,
  }

  public handleClose = () => {
    const { trackBankAdditionalVerification, country } = this.props
    const os = detectWebviewOs()
    const deeplink =
      os === OsTypes.Android ? ANDROID_CLOSE_DEEPLINK : IOS_CLOSE_DEEPLINK

    trackBankAdditionalVerification({
      country: COUNTRY_SHORTCODES[country],
      action: EventActions.Clicked,
      name: 'bank_additional_verification_closed',
      data: os,
      extra: deeplink,
    })
    if (this.props.handleDismiss) {
      this.props.handleDismiss()
    }
    if (os === OsTypes.Android) {
      window.location.assign(deeplink)
      return
    }

    if (os === OsTypes.iOS) {
      window.location.assign(deeplink)
    }
  }

  public setPhotoIdFront = file => {
    this.setState({
      photoIdFront: file,
    })
  }

  public setPhotoIdBack = file => {
    this.setState({
      photoIdBack: file,
    })
  }

  private goToNextStep = () => {
    const { trackBankAdditionalVerification, country, clearErrors } = this.props

    clearErrors()

    let step = this.state.currentStep
    if (step >= this.stepList.length - 1) {
      return this.handleClose()
    }
    step = this.state.currentStep + 1

    trackBankAdditionalVerification({
      country: COUNTRY_SHORTCODES[country],
      action: EventActions.Clicked,
      name: 'go_to_next_step',
      data: this.stepList[step].toString(),
    })
    this.setState(() => ({
      currentStep: step,
    }))
  }

  private router = currentStep => {
    const page = this.stepList[currentStep]

    switch (page) {
      case PageEnum.VerifyIdentity:
        return (
          <VerifyIdentity
            dismiss={this.handleClose}
            goToNextStep={this.goToNextStep}
          />
        )
      case PageEnum.IdentificationNumber:
        return (
          <IdentificationNumber
            country={this.country}
            goToNextStep={this.goToNextStep}
          />
        )
      case PageEnum.VerifyAddress:
        return <VerifyAddress goToNextStep={this.goToNextStep} />
      case PageEnum.GovernmentId:
        return (
          <GovernmentId
            goToNextStep={this.goToNextStep}
            setPhotoIdFront={this.setPhotoIdFront}
          />
        )
      case PageEnum.SubmitPhotoId:
        return (
          <SubmitPhotoId
            goToNextStep={this.goToNextStep}
            setPhotoIdFront={this.setPhotoIdFront}
            photoIdFront={this.state.photoIdFront}
          />
        )
      case PageEnum.SubmitPhotoIdBack:
        return (
          <SubmitPhotoIdBack
            goToNextStep={this.goToNextStep}
            setPhotoIdBack={this.setPhotoIdBack}
            photoIdBack={this.state.photoIdBack}
          />
        )
      case PageEnum.VerificationInProgress:
        return <VerificationInProgress goToNextStep={this.goToNextStep} />
      case PageEnum.VerificationComplete:
        return <VerificationComplete />
      default:
        return <VerifyIdentity goToNextStep={this.goToNextStep} />
    }
  }

  // tslint:disable-next-line:member-ordering
  public render() {
    const isShowingCloseButton =
      this.props.isDismissible && detectWebviewOs() !== OsTypes.NONE
    return (
      <React.Fragment>
        {isShowingCloseButton && (
          <CloseButton>
            <Icon type={Icon.Types.Close} onClick={this.handleClose} />
          </CloseButton>
        )}
        <Content>{this.router(this.state.currentStep)}</Content>
      </React.Fragment>
    )
  }
}

const mapStateToProps = state => ({
  application: state.application,
  country: state.application.country,
  stripeCredentials: state.stripeCredentials,
})

const mapDispatchToProps = {
  getStripeCredentials: actions.getStripeCredentials,
  getApplicationStatus: actions.getApplicationStatus,
  getPaymentAccount: actions.getPaymentAccount,
  trackBankAdditionalVerification: events.trackBankAdditionalVerification,
  clearErrors: actions.clearErrors,
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(BankAdditionalVerification)
